import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FaBroadcastTower,
  FaChevronLeft,
  FaChevronRight,
  FaPlayCircle,
  FaSyncAlt,
  FaVideo
} from 'react-icons/fa';
import { getOkamCameras, getOkamPlayback, getOkamStatus } from '../../api/okamService';

const statusLabels = {
  Loading: 'Kontrol ediliyor',
  Starting: 'Başlatılıyor',
  Connected: 'Bağlandı',
  Streaming: 'Canlı',
  Stopped: 'Yayın durduruldu',
  Unavailable: 'Yayın geçici olarak kullanılamıyor'
};

const normalizeStatus = (playback) => {
  const normalized = (playback?.state || '').trim().toLowerCase();

  if (normalized === 'streaming' && playback?.ffmpegRunning) return 'Streaming';
  if (normalized === 'streaming') return 'Starting';
  if (normalized === 'starting') return 'Starting';
  if (normalized === 'connected') return 'Connected';
  if (normalized === 'stopped') return 'Stopped';
  if (normalized === 'error' || playback?.lastError) return 'Unavailable';
  return 'Loading';
};

const isPlaybackStale = (playback) => {
  if ((playback?.state || '').trim().toLowerCase() !== 'streaming') {
    return false;
  }

  const lastFrameAt = Date.parse(playback?.lastFrameAt || '');
  if (Number.isFinite(lastFrameAt)) {
    return Date.now() - lastFrameAt > 45000;
  }

  const startedAt = Date.parse(playback?.startedAt || '');
  return Number.isFinite(startedAt) && Date.now() - startedAt > 25000;
};

const getCameraLocationName = (cameraName) => {
  const parts = (cameraName || '').split(' - ');
  return (parts[parts.length - 1] || cameraName || 'Kamera').trim();
};

const getCameraSectionName = (cameraName) => {
  const locationName = getCameraLocationName(cameraName);
  return locationName.replace(/\s+Kamera\s+[1-4]$/iu, '').trim();
};

const getCameraShortName = (cameraName) => {
  const locationName = getCameraLocationName(cameraName);
  const channelMatch = locationName.match(/\s+(Kamera\s+[1-4])$/iu);
  if (channelMatch) {
    return channelMatch[1];
  }

  const match = locationName.match(/\s+([İi]çerisi|[Dd]ışarısı|[Gg]irişi|[Çç]ıkışı)$/u);
  return match?.[1] || locationName;
};

const OkamPlayer = ({ camera }) => {
  const videoRef = useRef(null);
  const [playerError, setPlayerError] = useState('');
  const [needsUserPlay, setNeedsUserPlay] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !camera?.hlsUrl || camera?.status !== 'Streaming') {
      setPlayerError('');
      setNeedsUserPlay(false);
      if (video) {
        video.removeAttribute('src');
        video.load();
      }
      return undefined;
    }

    setPlayerError('');
    let hls;
    let disposed = false;
    let nativeRetryTimer;
    const getFreshHlsUrl = () => {
      const url = new URL(camera.hlsUrl, window.location.origin);
      url.searchParams.set('_t', Date.now().toString());
      return url.toString();
    };
    const tryPlay = () => {
      if (disposed) return;
      setPlayerError('');
      video.defaultPlaybackRate = 1;
      video.playbackRate = 1;
      video.play()
        .then(() => setNeedsUserPlay(false))
        .catch(() => {
          // iOS can reject even muted autoplay in Low Power/Data mode.
          // A direct user gesture is required in that case.
          if (!disposed) setNeedsUserPlay(true);
        });
    };
    const handleNativeReady = () => {
      tryPlay();
    };
    const enforceNormalPlaybackRate = () => {
      if (video.defaultPlaybackRate !== 1) video.defaultPlaybackRate = 1;
      if (video.playbackRate !== 1) video.playbackRate = 1;
    };
    const handlePlaying = () => {
      enforceNormalPlaybackRate();
      setNeedsUserPlay(false);
    };
    const handleNativeError = () => {
      if (disposed) return;
      setNeedsUserPlay(false);
      setPlayerError('Canlı yayın yeniden yükleniyor…');
      window.clearTimeout(nativeRetryTimer);
      nativeRetryTimer = window.setTimeout(() => {
        if (disposed) return;
        video.src = getFreshHlsUrl();
        video.load();
      }, 2000);
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible' || disposed) return;
      if (hls) hls.startLoad(-1);
      tryPlay();
    };

    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.defaultMuted = true;
      video.muted = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.src = getFreshHlsUrl();
      video.load();
      video.addEventListener('loadedmetadata', handleNativeReady);
      video.addEventListener('canplay', handleNativeReady);
      video.addEventListener('playing', handlePlaying);
      video.addEventListener('ratechange', enforceNormalPlaybackRate);
      video.addEventListener('error', handleNativeError);
    } else {
      import('hls.js').then(({ default: Hls }) => {
        if (disposed) return;

        if (!Hls.isSupported()) {
          setPlayerError('Tarayıcınız HLS canlı yayını desteklemiyor.');
          return;
        }

        hls = new Hls({
          enableWorker: true,
          lowLatencyMode: false,
          // The API already exposes a server-side delayed live edge.
          // Stay close to that delayed edge without accelerating playback.
          liveSyncDuration: 2,
          liveMaxLatencyDuration: 12,
          maxLiveSyncPlaybackRate: 1,
          maxBufferLength: 60,
          maxMaxBufferLength: 90,
          backBufferLength: 30
        });
        hls.loadSource(camera.hlsUrl);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, tryPlay);
        hls.on(Hls.Events.ERROR, (_, data) => {
          if (!data.fatal) return;

          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            setPlayerError('Canlı yayına bağlanılıyor…');
            hls.startLoad(-1);
          } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            setPlayerError('Görüntü yeniden hazırlanıyor…');
            hls.recoverMediaError();
          } else {
            setPlayerError('Canlı yayın şu anda oynatılamıyor.');
            hls.destroy();
          }
        });
      }).catch(() => {
        if (!disposed) setPlayerError('Canlı yayın oynatıcısı yüklenemedi.');
      });
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      disposed = true;
      window.clearTimeout(nativeRetryTimer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      video.removeEventListener('loadedmetadata', handleNativeReady);
      video.removeEventListener('canplay', handleNativeReady);
      video.removeEventListener('playing', handlePlaying);
      video.removeEventListener('ratechange', enforceNormalPlaybackRate);
      video.removeEventListener('error', handleNativeError);
      if (hls) hls.destroy();
      video.removeAttribute('src');
      video.load();
    };
  }, [camera?.hlsUrl, camera?.status]);

  const handleUserPlay = () => {
    const video = videoRef.current;
    if (!video) return;

    video.defaultMuted = true;
    video.muted = true;
    video.play()
      .then(() => {
        setNeedsUserPlay(false);
        setPlayerError('');
      })
      .catch(() => {
        setPlayerError('Canlı yayın başlatılamadı. Lütfen tekrar deneyin.');
      });
  };

  return (
    <div className="okam-player">
      <video
        ref={videoRef}
        className="okam-player__video"
        controls
        autoPlay
        muted
        playsInline
        preload="auto"
        aria-label={`${camera?.name || 'Aile Köyü'} canlı kamera yayını`}
      />
      {camera?.status === 'Streaming' && (
        <div className="okam-player__live">
          <span className="okam-player__pulse" />
          CANLI
        </div>
      )}
      {playerError && (
        <div className="okam-player__message">
          <FaPlayCircle />
          <span>{playerError}</span>
        </div>
      )}
      {needsUserPlay && !playerError && (
        <button
          type="button"
          className="okam-player__start"
          onClick={handleUserPlay}
        >
          <FaPlayCircle />
          <span>Canlı Yayını Başlat</span>
        </button>
      )}
    </div>
  );
};

const groupCamerasBySection = (cameras) => {
  const sections = new Map();

  cameras.forEach((camera) => {
    const name = getCameraSectionName(camera.name);
    if (!sections.has(name)) sections.set(name, []);
    sections.get(name).push(camera);
  });

  return Array.from(sections, ([name, sectionCameras]) => ({
    name,
    cameras: sectionCameras
  }));
};

const directSectionNames = [
  'Park Alanı',
  'Ahşap Atölyesi Dışarısı',
  'Ahşap Atölyesi İçerisi'
];

const findDirectSection = (sections, sectionNumber) => {
  const expectedName = directSectionNames[sectionNumber - 1];
  if (!expectedName) return null;

  return sections.find((section) => (
    section.name.localeCompare(expectedName, 'tr', { sensitivity: 'base' }) === 0
  )) || sections[sectionNumber - 1] || null;
};

const Okam = ({ sectionNumber = null }) => {
  const [cameras, setCameras] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState('');
  const [playbackByCamera, setPlaybackByCamera] = useState({});
  const [integrationEnabled, setIntegrationEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSectionName, setSelectedSectionName] = useState('');

  const loadCameras = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);

    try {
      const response = await getOkamCameras();
      setIntegrationEnabled(response.enabled);
      const allActiveCameras = response.enabled
        ? response.cameras.filter((camera) => camera.enabled)
        : [];
      const allSections = groupCamerasBySection(allActiveCameras);
      const directSection = Number.isInteger(sectionNumber)
        ? findDirectSection(allSections, sectionNumber)
        : null;
      const activeCameras = Number.isInteger(sectionNumber)
        ? (directSection?.cameras || [])
        : allActiveCameras;
      setCameras(activeCameras);
      setSelectedSectionName(directSection?.name || '');
      setSelectedCameraId((current) => (
        activeCameras.some((camera) => camera.id === current)
          ? current
          : activeCameras[0]?.id || ''
      ));
      setError('');
    } catch {
      setError('Canlı kamera bilgileri şu anda alınamıyor.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [sectionNumber]);

  useEffect(() => {
    loadCameras();
    const timer = window.setInterval(() => loadCameras({ silent: true }), 60000);
    return () => window.clearInterval(timer);
  }, [loadCameras]);

  const selectedCameraConfig = useMemo(() => {
    return cameras.find((camera) => camera.id === selectedCameraId) || cameras[0] || null;
  }, [cameras, selectedCameraId]);

  const cameraSections = useMemo(() => {
    return groupCamerasBySection(cameras);
  }, [cameras]);

  const selectedSection = useMemo(() => (
    cameraSections.find((section) => section.name === selectedSectionName) || null
  ), [cameraSections, selectedSectionName]);

  const selectedCamera = useMemo(() => {
    if (!selectedCameraConfig) return null;

    const playback = playbackByCamera[selectedCameraConfig.id];
    return {
      ...selectedCameraConfig,
      ...playback,
      status: playback?.status || 'Loading'
    };
  }, [playbackByCamera, selectedCameraConfig]);

  const isDirectSectionPage = Number.isInteger(sectionNumber);

  const loadPlayback = useCallback(async (
    cameraId,
    { start = false, restartIfUnhealthy = false } = {}
  ) => {
    if (!cameraId) return;

    try {
      let playback = start
        ? await getOkamPlayback(cameraId)
        : await getOkamStatus(cameraId);
      const normalizedStatus = normalizeStatus(playback);
      const shouldRestart =
        normalizedStatus === 'Stopped' ||
        normalizedStatus === 'Unavailable' ||
        isPlaybackStale(playback);
      if (!start && restartIfUnhealthy && shouldRestart) {
        playback = await getOkamPlayback(cameraId);
      }
      setPlaybackByCamera((current) => ({
        ...current,
        [cameraId]: {
          ...playback,
          status: normalizeStatus(playback)
        }
      }));
      setError('');
    } catch {
      setPlaybackByCamera((current) => ({
        ...current,
        [cameraId]: {
          ...(current[cameraId] || {}),
          status: 'Unavailable'
        }
      }));
    }
  }, []);

  useEffect(() => {
    const cameraId = selectedCameraConfig?.id;
    if (!cameraId) return undefined;

    loadPlayback(cameraId, { start: true });
    const timer = window.setInterval(
      () => loadPlayback(cameraId, { restartIfUnhealthy: true }),
      15000
    );
    return () => window.clearInterval(timer);
  }, [loadPlayback, selectedCameraConfig?.id]);

  const selectCamera = (cameraId) => {
    if (!cameraId || cameraId === selectedCameraConfig?.id) return;

    setPlaybackByCamera((current) => ({
      ...current,
      [cameraId]: {
        ...(current[cameraId] || {}),
        status: 'Loading'
      }
    }));
    setSelectedCameraId(cameraId);
  };

  const selectSection = (section) => {
    if (!section?.cameras?.length) return;

    setSelectedSectionName(section.name);
    selectCamera(section.cameras[0].id);
  };

  const renderCameraButton = (camera, useShortName = false) => (
    <button
      type="button"
      key={camera.id}
      className={camera.id === selectedCamera.id ? 'is-active' : ''}
      onClick={() => selectCamera(camera.id)}
    >
      <span className="okam-camera-list__icon"><FaBroadcastTower /></span>
      <span>
        <strong>{useShortName ? getCameraShortName(camera.name) : camera.name}</strong>
        <small>
          {statusLabels[playbackByCamera[camera.id]?.status] || 'Yayın noktası'}
        </small>
      </span>
    </button>
  );

  return (
    <main className="okam-page">
      <section className="container okam-content">
        {loading ? (
          <div className="okam-state">
            <span className="okam-spinner" />
            <h2>Canlı yayın hazırlanıyor</h2>
            <p>Kamera bağlantısı kontrol ediliyor.</p>
          </div>
        ) : error ? (
          <div className="okam-state okam-state--error">
            <FaBroadcastTower />
            <h2>Yayına ulaşılamadı</h2>
            <p>{error}</p>
            <button type="button" onClick={() => loadCameras()}>
              <FaSyncAlt /> Tekrar Dene
            </button>
          </div>
        ) : !integrationEnabled || !selectedCamera ? (
          <div className="okam-state">
            <FaBroadcastTower />
            <h2>Aktif yayın bulunmuyor</h2>
            <p>Aile Köyü canlı yayını etkinleştirildiğinde bu sayfada görüntülenecektir.</p>
          </div>
        ) : (
          <>
            {!isDirectSectionPage && (
            <div className={`okam-mobile-sections ${selectedSection ? 'has-selection' : ''}`}>
              {!selectedSection ? (
                <div className="okam-mobile-sections__grid">
                  {cameraSections.map((section) => (
                    <button
                      type="button"
                      key={section.name}
                      onClick={() => selectSection(section)}
                    >
                      <span className="okam-mobile-sections__card-icon"><FaVideo /></span>
                      <span className="okam-mobile-sections__card-copy">
                        <strong>{section.name}</strong>
                        <small>
                          {section.cameras.length} kamera · Canlı görüntüle
                        </small>
                      </span>
                      <span className="okam-mobile-sections__card-arrow">
                        <FaChevronRight />
                      </span>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="okam-mobile-sections__selected">
                  <button type="button" onClick={() => setSelectedSectionName('')}>
                    <FaChevronLeft /> Bölümlere Dön
                  </button>
                  <div>
                    <small>Seçili bölüm</small>
                    <strong>{selectedSection.name}</strong>
                  </div>
                </div>
              )}
            </div>
            )}

          <div className={`okam-grid ${selectedSection ? 'has-mobile-section' : ''} ${isDirectSectionPage ? 'is-direct-camera' : ''}`}>
            <div>
              <OkamPlayer camera={selectedCamera} />
              {isDirectSectionPage && cameras.length > 1 && (
                <div className="okam-direct-camera-tabs" role="tablist" aria-label="Kamera seçimi">
                  {cameras.map((camera, index) => (
                    <button
                      type="button"
                      role="tab"
                      aria-selected={camera.id === selectedCamera.id}
                      className={camera.id === selectedCamera.id ? 'is-active' : ''}
                      key={camera.id}
                      onClick={() => selectCamera(camera.id)}
                    >
                      <FaVideo />
                      <span>{getCameraShortName(camera.name) || `Kamera ${index + 1}`}</span>
                    </button>
                  ))}
                </div>
              )}
              <div className="okam-camera-info">
                <div>
                  <span className={`okam-status okam-status--${selectedCamera.status?.toLowerCase()}`}>
                    {statusLabels[selectedCamera.status] || selectedCamera.status}
                  </span>
                  <h2>{getCameraLocationName(selectedCamera.name)}</h2>
                </div>
                <p>
                  {selectedCamera.status === 'Streaming'
                    ? 'Aile Köyü canlı yayın bağlantısı aktif.'
                    : 'Aile Köyü kamerası hazırlanıyor.'}
                </p>
              </div>

              {!isDirectSectionPage && selectedSection?.cameras?.length > 1 && (
                <aside className="okam-camera-list okam-camera-list--mobile">
                  <h2>{selectedSection.name} Kameraları</h2>
                  {selectedSection.cameras.map((camera) => renderCameraButton(camera, true))}
                </aside>
              )}
            </div>

            {!isDirectSectionPage && cameras.length > 1 && (
              <aside className="okam-camera-list okam-camera-list--desktop">
                <h2>Aile Köyü Kameraları</h2>
                <div className="okam-camera-list__scroll">
                  {cameraSections.map((section) => (
                    <section className="okam-camera-list__section" key={section.name}>
                      <h3>{section.name}</h3>
                      {section.cameras.map((camera) => renderCameraButton(camera, true))}
                    </section>
                  ))}
                </div>
              </aside>
            )}
          </div>
          </>
        )}
      </section>
    </main>
  );
};

export default Okam;
